import React, { useState, useEffect } from "react";
import "./NewProject.css";
import "../common.css";
import { getUsersSummary, getAllDepartments } from "../../../api/api";
import {
  addProject,
  getProjectTypes,
  getAllBlocks,
  addBlock,
} from "../../../api/projectApi";
import { useNavigate } from "react-router-dom";
import Preloader from "../../Preloader/Preloader.js";
import Select from "react-select";
import Calendar from "../../Calendar/Calendar";
import CreateBlockModal from "../CreateBlockModal/CreateBlockModal";
import FinansistPayrollMapping from "../FinansistPayrollMapping/FinansistPayrollMapping";
import {
  buildFinansistPayrollMappingPayload,
  emptyFinansistMappingFormState,
} from "../../../utils/finansistPayrollMapping";

const NewProject = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);

  const [project, setProject] = useState({
    code: "",
    projectView: "",
    type: null, // Изменено на null, чтобы хранить объект типа проекта
    buildingType: "",
    name: "",
    contractNumber: "",
    cipher: "",
    responsible: "",
    startDate: "",
    expertiseStartDate: "",
    expertiseEndDate: "",
    endDate: "",
    blocks: [""],
    customer: "",
    signingDate: "", // Изменено с signingYear на signingDate
    contractAmount: "",
    status: "",
    projectInfo: "",
    departmentId: "",
  });

  const [users, setUsers] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showCreateBlockModal, setShowCreateBlockModal] = useState(false);
  const [createBlockSubmitting, setCreateBlockSubmitting] = useState(false);
  const [finansistMapping, setFinansistMapping] = useState(
    emptyFinansistMappingFormState,
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersData = await getUsersSummary({
          isActive: true,
          pageSize: 200,
        });
        setUsers(
          usersData.users.map((user) => ({
            value: user.id,
            label: `${user.surname} ${user.name}`,
          }))
        );

        // Возвращаем получение типов проектов
        const projectTypesData = await getProjectTypes();
        setProjectTypes(projectTypesData);

        // Получаем блоки
        const blocksData = await getAllBlocks();
        const activeBlocks = blocksData
          .filter((block) => block.is_active)
          .map((block) => ({
            value: block.id,
            label: block.name,
          }));
        setBlocks(activeBlocks);

        // Получаем отделения
        const departmentsData = await getAllDepartments();
        const activeDepartments = departmentsData
          .filter((dept) => dept.is_active)
          .map((dept) => ({
            value: dept.id,
            label: dept.name,
          }));
        setDepartments(activeDepartments);
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const validateField = (name, value) => {
    const requiredFields = [
      "code",
      "projectView",
      "type",
      "buildingType",
      "name",
      "contractNumber",
      "customer",
      "signingDate",
      "contractAmount",
      "status",
      "responsible",
      "startDate",
      "expertiseStartDate",
      "expertiseEndDate",
      "endDate",
      "departmentId",
      "blocks",
      "projectInfo",
    ];

    if (!requiredFields.includes(name)) {
      return "";
    }

    if (name === "type") {
      return value === null ? "Это поле обязательно для заполнения" : "";
    }

    if (name === "blocks") {
      // Проверяем, что массив не пустой и содержит хотя бы один непустой элемент
      if (!value || !Array.isArray(value) || value.length === 0) {
        return "Это поле обязательно для заполнения";
      }
      const hasValidBlock = value.some(block => block && block.trim() !== "");
      return hasValidBlock ? "" : "Это поле обязательно для заполнения";
    }

    if (!value || (typeof value === "string" && !value.trim())) {
      return "Это поле обязательно для заполнения";
    }

    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "code") {
      // Разрешаем цифры, латиницу, кириллицу (вкл. Ё/ё), дефис, слэш, подчеркивание и запятую
      newValue = value.replace(/[^a-zA-Zа-яА-ЯёЁ0-9\-/_,]/g, "");
    } else if (name === "contractAmount") {
      // Удаляем все нецифровые символы и ограничиваем длину 8 символами
      newValue = value.replace(/\D/g, "").slice(0, 9);
    } else if (name === "projectView") {
      if (
        value === "" ||
        value.toUpperCase() === "Б" ||
        value.toUpperCase() === "К" ||
        /^[Кк]\d*$/.test(value)
      ) {
        newValue = value.toUpperCase();
      } else {
        return;
      }
    }

    setProject((prev) => {
      const updatedProject = { ...prev, [name]: newValue };

      // Обновляем шифр проекта и удаляем пробелы и точки
      updatedProject.cipher = `ОД_${updatedProject.code}_${
        updatedProject.projectView
      }_${updatedProject.type ? updatedProject.type.name : ""}_${
        updatedProject.buildingType
      }_${updatedProject.name}_${updatedProject.customer}_${
        updatedProject.signingDate
          ? new Date(updatedProject.signingDate).getFullYear()
          : ""
      }`.replace(/[\s.]/g, "");

      return updatedProject;
    });

    // Валидация при изменении
    if (submitAttempted || touched[name]) {
      const error = validateField(name, newValue);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleBlur = (name, value) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const CREATE_BLOCK_OPTION = { value: "__create_block__", label: "+ Создать блок" };

  const handleBlockChange = async (selectedOptions) => {
    const options = selectedOptions || [];
    const createOpt = options.find((o) => o.value === CREATE_BLOCK_OPTION.value);
    if (createOpt) {
      setShowCreateBlockModal(true);
      return;
    }
    const newBlocks = options.map((option) => option.label);
    setProject((prev) => ({
      ...prev,
      blocks: newBlocks,
    }));
    if (submitAttempted || touched.blocks) {
      const error = validateField("blocks", newBlocks);
      setErrors((prev) => ({
        ...prev,
        blocks: error,
      }));
    }
  };

  const handleCreateBlock = async (nameNorm) => {
    setCreateBlockSubmitting(true);
    try {
      const newId = await addBlock(nameNorm, true);
      const blocksData = await getAllBlocks();
      const activeBlocks = blocksData
        .filter((block) => block.is_active)
        .map((block) => ({
          value: block.id,
          label: block.name,
        }));
      setBlocks(activeBlocks);
      const newBlock = activeBlocks.find(
        (b) => String(b.value) === String(newId) || b.label === nameNorm
      );
      if (newBlock) {
        setProject((prev) => ({
          ...prev,
          blocks: [...prev.blocks, newBlock.label],
        }));
        if (submitAttempted || touched.blocks) {
          const error = validateField("blocks", [...project.blocks, newBlock.label]);
          setErrors((prev) => ({ ...prev, blocks: error }));
        }
      }
      setShowCreateBlockModal(false);
    } catch (err) {
      alert("Ошибка создания блока: " + (err.message || err));
    } finally {
      setCreateBlockSubmitting(false);
    }
  };

  const validateAllFields = () => {
    const newErrors = {};
    const requiredFields = [
      "code",
      "projectView",
      "type",
      "buildingType",
      "name",
      "contractNumber",
      "customer",
      "signingDate",
      "contractAmount",
      "status",
      "responsible",
      "startDate",
      "expertiseStartDate",
      "expertiseEndDate",
      "endDate",
      "departmentId",
      "blocks",
      "projectInfo",
    ];

    requiredFields.forEach((field) => {
      const value = project[field];
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    setSubmitAttempted(true);
    return Object.keys(newErrors).length === 0;
  };

  const isDataValid = (data) => {
    const requiredFields = [
      "name",
      "cipher_pilot",
      "cipher_fin",
      "status",
      "date_start",
      "expert_start",
      "expert_end",
      "date_end",
      "information",
      "chief_id",
      "project_type_id",
      "department_id",
      "contract_amount",
      "contract_number",
      "building_type",
      "contract_year",
      "project_category",
      "customer",
      "blocks",
    ];

    for (let field of requiredFields) {
      if (
        data[field] === undefined ||
        data[field] === null ||
        data[field] === ""
      ) {
        console.error(`Поле ${field} отсутствует или пустое`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация всех полей
    if (!validateAllFields()) {
      alert("Пожалуйста, заполните все обязательные поля");
      return;
    }

    setIsLoading(true);

    try {
      // Функция для форматирования даты с учетом часового пояса
      const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        // Корректируем дату, чтобы избежать проблем с часовым поясом
        const adjustedDate = new Date(
          date.getTime() + date.getTimezoneOffset() * 60000
        );
        return adjustedDate.toISOString().split("T")[0];
      };

      const portalProjectName = `${project.code}_${project.buildingType}_${project.name}_${
        project.type ? project.type.name : ""
      }_${project.projectView}`;

      const projectData = {
        name: portalProjectName,
        cipher_pilot: project.code,
        cipher_fin: project.cipher,
        contract_amount: parseInt(project.contractAmount, 10) || 0,
        contract_number: project.contractNumber,
        building_type: project.buildingType,
        contract_year: formatDate(project.signingDate),
        project_category: project.projectView,
        status: project.status,
        date_start: formatDate(project.startDate),
        expert_start: formatDate(project.expertiseStartDate),
        expert_end: formatDate(project.expertiseEndDate),
        date_end: formatDate(project.endDate),
        information: project.projectInfo,
        customer: project.customer,
        chief_id: parseInt(project.responsible, 10),
        project_type_id: project.type ? project.type.id : null,
        department_id: project.departmentId ? parseInt(project.departmentId, 10) : null,
        blocks: project.blocks,
      };

      const finansistPayload = buildFinansistPayrollMappingPayload({
        portalProjectId: 0,
        portalProjectName,
        finansistProject: finansistMapping.finansistProject,
      });
      if (finansistPayload) {
        projectData.finansist_payroll_project_mappings = finansistPayload;
      }

      console.log(
        "Отправляемые данные проекта:",
        JSON.stringify(projectData, null, 2)
      );

      if (!isDataValid(projectData)) {
        alert("Пожалуйста, заполните все обязательные поля");
        return;
      }

      const responseData = await addProject(projectData);
      console.log("Проект успешно создан:", responseData);

      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        navigate("/projects/list");
      }, 3000);
    } catch (error) {
      console.error("Ошибка при создании проекта:", error);
      console.error("Полное содержимое ошибки:", error.message);
      alert(`Ошибка при создании проекта: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Функция для проверки заполнения всех обязтельных полей
  const isFormValid = () => {
    const requiredFields = [
      "code",
      "projectView",
      "type",
      "buildingType",
      "name",
      "contractNumber",
      "customer",
      "signingDate",
      "contractAmount",
      "status",
      "responsible",
      "startDate",
      "expertiseStartDate",
      "expertiseEndDate",
      "endDate",
      "departmentId",
    ];
    return requiredFields.every((field) => {
      if (field === "type") {
        return project[field] !== null;
      }
      const value = project[field];
      return value !== "" && value !== null && value !== undefined;
    });
  };

  const customStyles = (hasError) => ({
    control: (provided, state) => ({
      ...provided,
      background: "#F8F8F8",
      borderColor: hasError ? "#ff4d4f" : "#E0E0E0",
      borderRadius: "8px",
      padding: 0,
      fontFamily: "Manrope",
      fontSize: "14px",
      minHeight: "40px",
      height: "40px",
      boxShadow: "none",
      "&:hover": {
        borderColor: hasError ? "#ff4d4f" : "#2A5E38",
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      marginTop: "4px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#2A5E38"
        : state.isFocused
        ? "#f5f5f5"
        : "white",
      color: state.isSelected ? "white" : "#333",
      padding: "8px 12px",
      fontFamily: "Manrope",
      fontSize: "14px",
      cursor: "pointer",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      color: "#333",
      margin: 0,
      padding: "0 8px",
      lineHeight: "40px",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      color: "#999",
      margin: 0,
      padding: "0 8px",
      lineHeight: "40px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
      height: "38px",
      display: "flex",
      alignItems: "center",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: "0 8px",
      height: "38px",
      display: "flex",
      alignItems: "center",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
      height: "38px",
      display: "flex",
      alignItems: "center",
    }),
  });

  const multiSelectStyles = (hasError) => ({
    ...customStyles(hasError),
    control: (provided, state) => ({
      ...provided,
      background: "#F8F8F8",
      borderColor: "#E0E0E0",
      borderRadius: "8px",
      padding: 0,
      fontFamily: "Manrope",
      fontSize: "14px",
      minHeight: "40px",
      height:
        state.hasValue && state.selectProps.value.length > 2 ? "auto" : "40px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#2A5E38",
      },
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      padding: "2px 8px",
      minHeight: "38px",
      height:
        state.hasValue && state.selectProps.value.length > 2 ? "auto" : "38px",
      display: "flex",
      alignItems: "flex-start",
      flexWrap: "wrap",
      gap: "2px",
      paddingTop: "2px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#E8E8F8",
      borderRadius: "4px",
      margin: "2px",
      height: "28px",
      display: "flex",
      alignItems: "center",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#333",
      padding: "2px 6px",
      fontSize: "14px",
      fontFamily: "Manrope",
      lineHeight: "24px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      borderRadius: "0 4px 4px 0",
      padding: "0 4px",
      height: "28px",
      display: "flex",
      alignItems: "center",
      "&:hover": {
        backgroundColor: "#2A5E38",
        color: "white",
      },
    }),
    indicatorsContainer: (provided, state) => ({
      ...provided,
      height:
        state.hasValue && state.selectProps.value.length > 2 ? "100%" : "38px",
      alignItems: "center",
      alignSelf: "flex-start",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
      height: "38px",
      display: "flex",
      alignItems: "center",
      alignSelf: "flex-start",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
      height: "38px",
      display: "flex",
      alignItems: "center",
      alignSelf: "flex-start",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: "2px 6px",
      height: "32px",
      display: "flex",
      alignItems: "center",
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: 0,
      lineHeight: "38px",
    }),
  });

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="project-form-container">
      <form className="project-form" onSubmit={handleSubmit}>
        {showPopup && (
          <div className="popup-overlay">
            <div className="popup">
              <div className="popup-icon"></div>
              <p className="popup-text">Проект успешно создан</p>
            </div>
          </div>
        )}

        <ul className="newproject__points">
          <li className="newproject__point newproject__point--full-width">
            <span className="newproject__point-title">
              Шифр проекта (Finansist)
            </span>
            <input
              name="cipher"
              value={project.cipher}
              readOnly
              placeholder="Шифр будет сгенерирован автоматически"
            />
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Тип проекта</span>
            <Select
              name="type"
              options={projectTypes.map((type) => ({
                value: type.id,
                label: type.name,
              }))}
              styles={customStyles(!!errors.type && (submitAttempted || touched.type))}
              value={
                project.type
                  ? { value: project.type.id, label: project.type.name }
                  : null
              }
              onChange={(selectedOption) => {
                const selectedType = projectTypes.find(
                  (type) => type.id === selectedOption.value
                );
                setProject((prev) => {
                  const updatedProject = { ...prev, type: selectedType };
                  updatedProject.cipher = `ОД_${updatedProject.code}_${
                    updatedProject.projectView
                  }_${updatedProject.type ? updatedProject.type.name : ""}_${
                    updatedProject.buildingType
                  }_${updatedProject.name}_${updatedProject.customer}_${
                    updatedProject.signingDate
                      ? new Date(updatedProject.signingDate).getFullYear()
                      : ""
                  }`.replace(/[\s.]/g, "");
                  return updatedProject;
                });
                
                // Валидация при изменении
                if (submitAttempted || touched.type) {
                  const error = validateField("type", selectedType);
                  setErrors((prev) => ({
                    ...prev,
                    type: error,
                  }));
                }
              }}
              onBlur={() => handleBlur("type", project.type)}
              placeholder="Выберите тип проекта"
              noOptionsMessage={() => "Типы проектов не найдены"}
            />
            {errors.type && (submitAttempted || touched.type) && (
              <span className="newproject__error-message">{errors.type}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Тип здания</span>
            <Select
              name="buildingType"
              options={[
                { value: "МЖК", label: "МЖК" },
                { value: "ИЖД", label: "ИЖД" },
                { value: "ОЗ", label: "ОЗ" },
                { value: "-", label: "-" },
              ]}
              styles={customStyles(!!errors.buildingType && (submitAttempted || touched.buildingType))}
              value={
                project.buildingType
                  ? { value: project.buildingType, label: project.buildingType }
                  : null
              }
              onChange={(selectedOption) =>
                handleInputChange({
                  target: { name: "buildingType", value: selectedOption.value },
                })
              }
              onBlur={() => handleBlur("buildingType", project.buildingType)}
              placeholder="Выберите тип здания"
              noOptionsMessage={() => "Типы зданий не найдены"}
            />
            {errors.buildingType && (submitAttempted || touched.buildingType) && (
              <span className="newproject__error-message">{errors.buildingType}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Код проекта</span>
            <input
              name="code"
              value={project.code}
              onChange={handleInputChange}
              onBlur={() => handleBlur("code", project.code)}
              className={errors.code && (submitAttempted || touched.code) ? "newproject__input-error" : ""}
              placeholder="Введите код проекта"
              required
            />
            {errors.code && (submitAttempted || touched.code) && (
              <span className="newproject__error-message">{errors.code}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Вид проекта</span>
            <input
              name="projectView"
              value={project.projectView}
              onChange={handleInputChange}
              onBlur={() => handleBlur("projectView", project.projectView)}
              className={errors.projectView && (submitAttempted || touched.projectView) ? "newproject__input-error" : ""}
              placeholder="Введите Б или К(с номером)"
              required
            />
            {errors.projectView && (submitAttempted || touched.projectView) && (
              <span className="newproject__error-message">{errors.projectView}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Название проекта</span>
            <input
              name="name"
              value={project.name}
              onChange={handleInputChange}
              onBlur={() => handleBlur("name", project.name)}
              className={errors.name && (submitAttempted || touched.name) ? "newproject__input-error" : ""}
              placeholder="Введите название проекта"
              required
            />
            {errors.name && (submitAttempted || touched.name) && (
              <span className="newproject__error-message">{errors.name}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Номер договора</span>
            <input
              name="contractNumber"
              value={project.contractNumber}
              onChange={handleInputChange}
              onBlur={() => handleBlur("contractNumber", project.contractNumber)}
              className={errors.contractNumber && (submitAttempted || touched.contractNumber) ? "newproject__input-error" : ""}
              placeholder="Введите номер договора"
              required
            />
            {errors.contractNumber && (submitAttempted || touched.contractNumber) && (
              <span className="newproject__error-message">{errors.contractNumber}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Заказчик</span>
            <input
              name="customer"
              value={project.customer}
              onChange={handleInputChange}
              onBlur={() => handleBlur("customer", project.customer)}
              className={errors.customer && (submitAttempted || touched.customer) ? "newproject__input-error" : ""}
              placeholder="Введите заказчика"
              required
            />
            {errors.customer && (submitAttempted || touched.customer) && (
              <span className="newproject__error-message">{errors.customer}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Сумма по договору</span>
            <input
              name="contractAmount"
              value={project.contractAmount}
              onChange={handleInputChange}
              onBlur={() => handleBlur("contractAmount", project.contractAmount)}
              className={errors.contractAmount && (submitAttempted || touched.contractAmount) ? "newproject__input-error" : ""}
              placeholder="Введите сумму"
              required
            />
            {errors.contractAmount && (submitAttempted || touched.contractAmount) && (
              <span className="newproject__error-message">{errors.contractAmount}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Статус</span>
            <Select
              name="status"
              options={[
                { value: "Перспективный", label: "Перспективный" },
                { value: "Разработка", label: "Разработка" },
                { value: "Экспертиза", label: "Экспертиза" },
                { value: "Завершен", label: "Завершен" },
                { value: "Авторский надзор", label: "Авторский надзор" },
                { value: "Корректировка", label: "Корректировка" },
                { value: "Приостановлен", label: "Приостановлен" },
                { value: "Отказ", label: "Отказ" },
              ]}
              styles={customStyles(!!errors.status && (submitAttempted || touched.status))}
              value={
                project.status
                  ? { value: project.status, label: project.status }
                  : null
              }
              onChange={(selectedOption) =>
                handleInputChange({
                  target: { name: "status", value: selectedOption.value },
                })
              }
              onBlur={() => handleBlur("status", project.status)}
              placeholder="Выберите статус"
              noOptionsMessage={() => "Статусы не найдены"}
            />
            {errors.status && (submitAttempted || touched.status) && (
              <span className="newproject__error-message">{errors.status}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Ответственный</span>
            <Select
              name="responsible"
              options={users}
              styles={customStyles(!!errors.responsible && (submitAttempted || touched.responsible))}
              value={users.find((user) => user.value === project.responsible)}
              onChange={(selectedOption) =>
                handleInputChange({
                  target: { name: "responsible", value: selectedOption.value },
                })
              }
              onBlur={() => handleBlur("responsible", project.responsible)}
              placeholder="Выберите ответственного"
              noOptionsMessage={() => "Пользователи не найдены"}
            />
            {errors.responsible && (submitAttempted || touched.responsible) && (
              <span className="newproject__error-message">{errors.responsible}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Отделение</span>
            <Select
              name="departmentId"
              options={departments}
              styles={customStyles(!!errors.departmentId && (submitAttempted || touched.departmentId))}
              value={departments.find((dept) => dept.value === project.departmentId)}
              onChange={(selectedOption) =>
                handleInputChange({
                  target: { name: "departmentId", value: selectedOption ? selectedOption.value : "" },
                })
              }
              onBlur={() => handleBlur("departmentId", project.departmentId)}
              placeholder="Выберите отделение"
              noOptionsMessage={() => "Отделения не найдены"}
              isClearable
            />
            {errors.departmentId && (submitAttempted || touched.departmentId) && (
              <span className="newproject__error-message">{errors.departmentId}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Дата подписания</span>
            <div className={errors.signingDate && (submitAttempted || touched.signingDate) ? "newproject__calendar-error-wrapper" : ""}>
              <Calendar
                value={project.signingDate ? new Date(project.signingDate) : null}
                onChange={(date) => {
                  handleInputChange({
                    target: {
                      name: "signingDate",
                      value: date.toISOString().split("T")[0],
                    },
                  });
                  if (submitAttempted || touched.signingDate) {
                    const error = validateField("signingDate", date.toISOString().split("T")[0]);
                    setErrors((prev) => ({
                      ...prev,
                      signingDate: error,
                    }));
                  }
                }}
                onBlur={() => handleBlur("signingDate", project.signingDate)}
                className={errors.signingDate && (submitAttempted || touched.signingDate) ? "newproject__input-error" : ""}
                required
              />
            </div>
            {errors.signingDate && (submitAttempted || touched.signingDate) && (
              <span className="newproject__error-message">{errors.signingDate}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Дата начала</span>
            <div className={errors.startDate && (submitAttempted || touched.startDate) ? "newproject__calendar-error-wrapper" : ""}>
              <Calendar
                value={project.startDate ? new Date(project.startDate) : null}
                onChange={(date) => {
                  handleInputChange({
                    target: {
                      name: "startDate",
                      value: date.toISOString().split("T")[0],
                    },
                  });
                  if (submitAttempted || touched.startDate) {
                    const error = validateField("startDate", date.toISOString().split("T")[0]);
                    setErrors((prev) => ({
                      ...prev,
                      startDate: error,
                    }));
                  }
                }}
                onBlur={() => handleBlur("startDate", project.startDate)}
                className={errors.startDate && (submitAttempted || touched.startDate) ? "newproject__input-error" : ""}
                required
              />
            </div>
            {errors.startDate && (submitAttempted || touched.startDate) && (
              <span className="newproject__error-message">{errors.startDate}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Начало экспертизы</span>
            <div className={errors.expertiseStartDate && (submitAttempted || touched.expertiseStartDate) ? "newproject__calendar-error-wrapper" : ""}>
              <Calendar
                value={
                  project.expertiseStartDate
                    ? new Date(project.expertiseStartDate)
                    : null
                }
                onChange={(date) => {
                  handleInputChange({
                    target: {
                      name: "expertiseStartDate",
                      value: date.toISOString().split("T")[0],
                    },
                  });
                  if (submitAttempted || touched.expertiseStartDate) {
                    const error = validateField("expertiseStartDate", date.toISOString().split("T")[0]);
                    setErrors((prev) => ({
                      ...prev,
                      expertiseStartDate: error,
                    }));
                  }
                }}
                onBlur={() => handleBlur("expertiseStartDate", project.expertiseStartDate)}
                className={errors.expertiseStartDate && (submitAttempted || touched.expertiseStartDate) ? "newproject__input-error" : ""}
                required
              />
            </div>
            {errors.expertiseStartDate && (submitAttempted || touched.expertiseStartDate) && (
              <span className="newproject__error-message">{errors.expertiseStartDate}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Дата окончания</span>
            <div className={errors.endDate && (submitAttempted || touched.endDate) ? "newproject__calendar-error-wrapper" : ""}>
              <Calendar
                value={project.endDate ? new Date(project.endDate) : null}
                onChange={(date) => {
                  handleInputChange({
                    target: {
                      name: "endDate",
                      value: date.toISOString().split("T")[0],
                    },
                  });
                  if (submitAttempted || touched.endDate) {
                    const error = validateField("endDate", date.toISOString().split("T")[0]);
                    setErrors((prev) => ({
                      ...prev,
                      endDate: error,
                    }));
                  }
                }}
                onBlur={() => handleBlur("endDate", project.endDate)}
                className={errors.endDate && (submitAttempted || touched.endDate) ? "newproject__input-error" : ""}
                required
              />
            </div>
            {errors.endDate && (submitAttempted || touched.endDate) && (
              <span className="newproject__error-message">{errors.endDate}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Конец экспертизы</span>
            <div className={errors.expertiseEndDate && (submitAttempted || touched.expertiseEndDate) ? "newproject__calendar-error-wrapper" : ""}>
              <Calendar
                value={
                  project.expertiseEndDate
                    ? new Date(project.expertiseEndDate)
                    : null
                }
                onChange={(date) => {
                  handleInputChange({
                    target: {
                      name: "expertiseEndDate",
                      value: date.toISOString().split("T")[0],
                    },
                  });
                  if (submitAttempted || touched.expertiseEndDate) {
                    const error = validateField("expertiseEndDate", date.toISOString().split("T")[0]);
                    setErrors((prev) => ({
                      ...prev,
                      expertiseEndDate: error,
                    }));
                  }
                }}
                onBlur={() => handleBlur("expertiseEndDate", project.expertiseEndDate)}
                className={errors.expertiseEndDate && (submitAttempted || touched.expertiseEndDate) ? "newproject__input-error" : ""}
                required
              />
            </div>
            {errors.expertiseEndDate && (submitAttempted || touched.expertiseEndDate) && (
              <span className="newproject__error-message">{errors.expertiseEndDate}</span>
            )}
          </li>
          <li className="newproject__point">
            <span className="newproject__point-title">Блоки проекта</span>
            <Select
              isMulti
              name="blocks"
              options={[CREATE_BLOCK_OPTION, ...blocks]}
              value={blocks.filter((block) =>
                project.blocks.includes(block.label)
              )}
              onChange={handleBlockChange}
              onBlur={() => handleBlur("blocks", project.blocks)}
              styles={multiSelectStyles(!!errors.blocks && (submitAttempted || touched.blocks))}
              placeholder="Выберите блоки проекта"
              noOptionsMessage={() => "Блоки не найдены"}
            />
            {errors.blocks && (submitAttempted || touched.blocks) && (
              <span className="newproject__error-message">{errors.blocks}</span>
            )}
          </li>
          <FinansistPayrollMapping
            variant="newproject"
            value={finansistMapping}
            onChange={setFinansistMapping}
            selectStyles={customStyles}
          />
          <li className="newproject__point newproject__point--full-width">
            <span className="newproject__point-title">
              Информация о проекте
            </span>
            <textarea
              name="projectInfo"
              value={project.projectInfo}
              onChange={handleInputChange}
              onBlur={() => handleBlur("projectInfo", project.projectInfo)}
              className={errors.projectInfo && (submitAttempted || touched.projectInfo) ? "newproject__input-error" : ""}
              placeholder="Введите информацию о проекте"
              rows="4"
            />
            {errors.projectInfo && (submitAttempted || touched.projectInfo) && (
              <span className="newproject__error-message">{errors.projectInfo}</span>
            )}
          </li>
        </ul>

        <div className="newproject__buttons">
          <button
            type="submit"
            className="newproject__button button-send"
            disabled={!isFormValid()}
          >
            Создать
          </button>
          <button type="reset" className="newproject__button button-reset">
            Сбросить
          </button>
        </div>
      </form>

      <CreateBlockModal
        open={showCreateBlockModal}
        onClose={() => setShowCreateBlockModal(false)}
        existingBlocks={blocks}
        onCreate={handleCreateBlock}
        isSubmitting={createBlockSubmitting}
      />
    </div>
  );
};

export default NewProject;
